import { Canvas } from './Canvas'
import {
  useCurrentCanvasSide,
  useDesignSide,
  useEditorConfig,
  useMultiColorMode,
  useWholeCanvasConfig,
  useWidget,
} from './EditorDataProvider'
import React, { useEffect, useState } from 'react'
import { ControlsContainer } from './Controls/ControlsContainer'
import { PatternSettings } from './Controls/PatternSettings'
import { LogoSettings } from './Controls/LogoSettings'
import { CustomizeButton } from './Controls/CustomizeButton'
import {
  useEditorFullScreenMode,
  useOnlineEditorState,
} from '../UnifiedEditorProvider'
import { Loader, ErrorState, getArtworkDPI } from './editor.utils'
import { SettingsContainer } from './Controls/SettingsControl/SettingsContainer'
import { SettingsButton } from './Controls/SettingsControl/SettingsButton'
import { SettingsControl } from './Controls/SettingsControl/SettingsControl'
import { patternInitialStateLogo } from './initialAssets/onlineEditorInitialStateLogo'
import { ColorPickerContainer } from './Controls/ColorPicker/ColorPickerContainer'
import camelCase from 'lodash/camelCase'
import { ColorButton } from './Controls/ColorPicker/ColorButton'
import { ColorPicker } from './Controls/ColorPicker/ColorPicker'
import { trackDesignerInteraction } from '@utils/gtm'
import router from 'next/router'
import { ImageConversionState } from '../designUpload/components/UploadDropzone/UploadDropzone'
import { DoneButton } from './Controls/DoneButton'
import { TopControlBar } from './Controls/TopControlBar'
import { UnifiedColorPicker } from '../unifiedColorPicker/unifiedColorPicker'
import { useDevice } from '@utils/responsiveness/useDevice'
import { ARTBOARD_SIZE, initialMultiColorAssetUrl } from './editor.config'
import { TDesignSide } from './editor.types'
import classnames from 'classnames'
import { useSelectedFile } from '../designUpload/components/UploadDropzone/UploadDropzone.provider'
import { HiOutlineExclamationCircle, HiOutlineX } from 'react-icons/hi'
import { Transition } from '@headlessui/react'
import { mmToInches } from '@utils/utilities'

export function Editor({ readOnlyMode = false }: { readOnlyMode?: boolean }) {
  const editorConfig = useEditorConfig()
  const { multiColorModeIsOn } = useMultiColorMode()
  const { activeDesignSide } = useDesignSide()
  const {
    canvasConfig: currentCanvasConfig,
    updateCanvasConfig: updateCurrentCanvas,
  } = useCurrentCanvasSide()

  const { wholeCanvasConfig, updateBothCanvasSides } = useWholeCanvasConfig()

  const { widget, updateWidget } = useWidget(
    currentCanvasConfig.activeWidgetType
  )
  const { productColorConfig } = editorConfig
  const { isMobile } = useDevice()
  const { isFullScreen, setIsFullScreen } = useEditorFullScreenMode()
  const [controlToShow, setControlToShow] = useState<
    'pattern' | 'settings' | 'logoColor' | 'backgroundColor' | null
  >(null)
  const { editorState, setEditorState } = useOnlineEditorState()
  const { selectedFile } = useSelectedFile()
  const [showResolutionWarning, setShowResolutionWarning] = useState(false)

  useEffect(() => {
    if (selectedFile) {
      updateWidget({
        assetUrl: selectedFile?.fileAsDataUrl || initialMultiColorAssetUrl,
        assetSvgData: selectedFile?.fileAsSVG || patternInitialStateLogo,
      })
    }
  }, [selectedFile?.uploadedFileUrl, selectedFile?.fileAsSVG])

  useEffect(() => {
    if (selectedFile) {
      if (
        selectedFile?.fileConversionState === ImageConversionState.NOT_CONVERTED
      ) {
        setEditorState('loading')
      } else if (
        selectedFile?.fileAsSVG &&
        selectedFile?.fileAsDataUrl &&
        selectedFile?.fileConversionState ===
          ImageConversionState.CONVERSION_SUCCESS
      ) {
        setEditorState('loaded-with-file')
      } else if (
        selectedFile?.fileConversionState ===
        ImageConversionState.CONVERSION_ERROR
      ) {
        setEditorState('error')
      } else {
        setEditorState('initial')
      }
    } else {
      setEditorState('initial')
    }
  }, [selectedFile?.fileConversionState])

  useEffect(() => {
    setControlToShow(null)
  }, [productColorConfig])

  useEffect(() => {
    if (!isFullScreen) {
      setControlToShow(null)
    }
  }, [isFullScreen])

  const [canvasTransition, setCanvasTransition] = useState(false)
  const { setActiveDesignSide } = useDesignSide()

  const changeCanvasSide = (canvasSide: TDesignSide) => {
    setCanvasTransition(true)

    setTimeout(() => {
      setActiveDesignSide(canvasSide)
    }, 250)
    setTimeout(() => {
      setCanvasTransition(false)
    }, 500)
  }

  const updateColors = (newColor) => {
    if (editorConfig?.sidesShareColors) {
      const colorKey = Object?.keys(newColor)?.[0]
      const hiddenDesignSide = activeDesignSide === 'front' ? 'back' : 'front'
      const currentColor =
        wholeCanvasConfig[hiddenDesignSide]?.[colorKey]?.value
      const newColorValue = newColor[colorKey]?.value

      updateBothCanvasSides({
        [activeDesignSide]: {
          ...wholeCanvasConfig[activeDesignSide],
          ...newColor,
        },
        [hiddenDesignSide]: {
          ...wholeCanvasConfig[hiddenDesignSide],
          ...newColor,
          canvasSvgData: wholeCanvasConfig[
            hiddenDesignSide
          ]?.canvasSvgData?.replaceAll(
            `fill="${currentColor}"`,
            `fill="${newColorValue}"`
          ),
        },
      })
    } else {
      updateCurrentCanvas(newColor)
    }
  }

  const [artworkDpi, setArtworkDpi] = useState<number>(0)

  useEffect(() => {
    if (
      editorConfig?.productWidthInMm &&
      ['jpeg', 'jpg', 'png'].includes(selectedFile?.metaData?.format)
    ) {
      setArtworkDpi(
        getArtworkDPI({
          productWidthInInches: mmToInches(editorConfig.productWidthInMm),
          file: selectedFile,
        })
      )
    } else {
      setArtworkDpi(200)
    }
  }, [editorConfig?.productWidthInMm, selectedFile])

  useEffect(() => {
    if (multiColorModeIsOn && artworkDpi && artworkDpi < 150) {
      setShowResolutionWarning(true)
    } else {
      setShowResolutionWarning(false)
    }
  }, [multiColorModeIsOn, artworkDpi])

  return (
    <div className={`${!isFullScreen && 'sticky'}`}>
      <div
        className={`fixed top-0 bottom-0 left-0 right-0 z-modal-overlay bg-core-grey-90 ${
          isFullScreen ? 'visible opacity-30' : 'invisible opacity-0'
        } transition-all sm:duration-200 duration-400`}
        onClick={() => {
          setIsFullScreen(false)
        }}
      />
      <div
        className={`flex items-center justify-center rounded-2xl lg:rounded-[24px] bg-core-grey-10 border-2 lg:border-4 border-core-grey-30 p-3.5 md:p-10 ${
          isFullScreen
            ? 'z-modal-overlay fixed inset-x-48 inset-y-20'
            : 'w-full'
        }`}
      >
        <div className="relative flex flex-col w-full h-full">
          <TopControlBar changeCanvasSide={changeCanvasSide} />

          <div
            className={`w-full bg-core-white border-2 border-core-purple-10 rounded-[16px] mb-5 relative overflow-hidden shrink-0 grow`}
            style={{
              width: '100%',
              height: isFullScreen
                ? 'calc(100% - 110px)'
                : ARTBOARD_SIZE.height + 'px',
            }}
            onClick={() => {
              setControlToShow(null)
            }}
          >
            <div className="absolute top-0 bottom-0 w-full">
              {editorState === 'loading' && <Loader />}
              {editorState === 'error' && <ErrorState />}

              {(editorState === 'loaded-with-file' ||
                editorState === 'initial') && (
                <>
                  <Canvas
                    className={classnames({
                      'transition-all duration-[.25s] ease-in-out opacity-100':
                        true,
                      'opacity-100': !canvasTransition,
                      'opacity-0': canvasTransition,
                    })}
                  />
                </>
              )}

              <Transition
                show={showResolutionWarning}
                enter="transition-all durration-300 ease-linear"
                enterFrom="-bottom-36"
                enterTo="bottom-7"
                leave=""
                leaveFrom="bottom-7"
                leaveTo="-bottom-36"
                className="flex bottom-7 justify-start items-center absolute w-[92%] rounded-full left-1/2 -translate-x-1/2 bg-pitaya-5 border-pitaya-20 border py-8 pl-6 pr-10"
              >
                <HiOutlineExclamationCircle className="text-[25px] text-pitaya shrink-0 mr-5" />

                <p className="text-black font-mori text-lg font-normal mr-2">
                  We noticed this design file is low resolution and may print
                  with graininess and pixelation. Try uploading a higher
                  resolution version.
                </p>

                <HiOutlineX
                  className="text-[25px] text-black shrink-0 cursor-pointer ml-auto"
                  onClick={() => setShowResolutionWarning(false)}
                />
              </Transition>

              <ControlsContainer
                showControls={controlToShow === 'pattern'}
                setShowControls={() => {
                  if (controlToShow !== 'pattern') {
                    setControlToShow('pattern')
                  } else {
                    setControlToShow(null)
                  }
                }}
              >
                {currentCanvasConfig.activeWidgetType === 'pattern' && (
                  <PatternSettings patternVariant={widget?.config?.variant} />
                )}

                {currentCanvasConfig.activeWidgetType === 'logo' && (
                  <LogoSettings />
                )}
              </ControlsContainer>

              <SettingsContainer showSettings={controlToShow === 'settings'}>
                <SettingsControl />
              </SettingsContainer>

              <ColorPickerContainer
                showControls={
                  (controlToShow === 'backgroundColor' ||
                    controlToShow === 'logoColor') &&
                  Boolean(
                    productColorConfig?.find(
                      (picker) => camelCase(picker.key) === controlToShow
                    )
                  )
                }
              >
                <div className={`relative pointer-events-auto h-full`}>
                  {productColorConfig?.map((colorPickerConfig) => {
                    if (controlToShow !== camelCase(colorPickerConfig.key)) {
                      return null
                    }

                    return (
                      <ColorPicker
                        key={
                          colorPickerConfig.key ||
                          colorPickerConfig?.pickerLabel
                        }
                        canvasConfig={currentCanvasConfig}
                        colorPickerConfig={colorPickerConfig}
                        onClose={() => {
                          setControlToShow(null)
                        }}
                        onSelected={(color, label) => {
                          updateColors({
                            [camelCase(colorPickerConfig.key)]: {
                              value: color?.value ? color?.value : color,
                              label: color?.label ? color?.label : label,
                            },
                          })
                        }}
                      />
                    )
                  })}
                </div>
              </ColorPickerContainer>
            </div>
          </div>

          <div
            className={`flex flex-col flex-wrap items-center justify-start w-full gap-4 sm:flex-row ${
              readOnlyMode ? 'pointer-events-none' : ''
            }`}
          >
            <CustomizeButton
              showControls={controlToShow === 'pattern'}
              setShowControls={() => {
                if (controlToShow !== 'pattern') {
                  if (!isFullScreen && !isMobile) {
                    setIsFullScreen(true)
                  }
                  setControlToShow('pattern')
                } else {
                  setControlToShow(null)
                }
              }}
            />

            {!multiColorModeIsOn &&
              productColorConfig?.map((colorPickerConfig) => {
                const colorType = camelCase(colorPickerConfig.key)

                return (
                  <div key={colorType} className="w-full sm:w-auto">
                    <ColorButton
                      key={colorType}
                      color={currentCanvasConfig[colorType]?.value}
                      showColorSetting={controlToShow === colorType}
                      setShowColorSetting={() => {
                        if (controlToShow !== colorType) {
                          setControlToShow(
                            colorType as 'logoColor' | 'backgroundColor'
                          )
                        } else {
                          setControlToShow(null)
                        }
                      }}
                    />

                    {controlToShow === colorType && (
                      <div className="w-full sm:hidden">
                        <UnifiedColorPicker
                          tabs={colorPickerConfig.pickerConfig}
                          initialColors={
                            [
                              currentCanvasConfig[
                                camelCase(colorPickerConfig.key)
                              ],
                            ] || null
                          }
                          onSelected={(color) => {
                            if (color.value) {
                              updateColors({
                                [camelCase(colorPickerConfig.key)]: color,
                              })
                              trackDesignerInteraction({
                                event: 'designer-interaction',
                                designerName: 'editor',
                                componentName: `${camelCase(
                                  colorPickerConfig.key
                                )} Color Picker`,
                                componentInformation:
                                  color.value || color.label,
                                selectedProductSlug:
                                  router.query?.slug.toString(),
                                displayType: 'ColorSelector',
                              })
                            }
                          }}
                          onClose={() => setControlToShow(null)}
                        />
                      </div>
                    )}
                  </div>
                )
              })}

            <SettingsButton
              showSettings={controlToShow === 'settings'}
              setShowSetting={() => {
                if (controlToShow !== 'settings') {
                  setControlToShow('settings')
                } else {
                  setControlToShow(null)
                }
              }}
            />

            <DoneButton
              onDone={() => {
                setControlToShow(null)
                setIsFullScreen(false)
                trackDesignerInteraction({
                  event: 'designer-interaction',
                  designerName: 'editor',
                  componentName: `ExitFullScreenMode`,
                  componentInformation:
                    'User exited online editor full screen mode',
                  selectedProductSlug: router.query?.slug.toString(),
                  displayType: 'ButtonTilesSelector',
                })
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
